<template>
    <div class="user-management">
        <!-- 使用通用头部组件 -->
        <AdminPageHeader title="用户管理" subtitle="管理系统用户">
            <template #actions>
                <a-space>
                    <a-button @click="loadUsers" :icon="h(ReloadOutlined)">
                        刷新
                    </a-button>
                </a-space>
            </template>
        </AdminPageHeader>

        <div class="user-page-content">
            <!-- 统计卡片 -->
            <a-row :gutter="[16, 16]" class="stats-cards">
                <a-col :xs="24" :sm="8" :md="8">
                    <a-card :bordered="false">
                        <a-statistic title="总用户数" :value="totalUsers" />
                    </a-card>
                </a-col>
                <a-col :xs="24" :sm="8" :md="8">
                    <a-card :bordered="false">
                        <a-statistic title="今日注册" :value="todayRegistered" />
                    </a-card>
                </a-col>
                <a-col :xs="24" :sm="8" :md="8">
                    <a-card :bordered="false">
                        <a-statistic title="本月注册" :value="thisMonthRegistered" />
                    </a-card>
                </a-col>
            </a-row>

            <!-- 注册趋势图表 -->
            <a-card title="用户注册趋势" class="chart-card" :bordered="false">
                <div class="chart-container">
                    <canvas ref="registrationChartRef" width="400" height="200"></canvas>
                </div>
            </a-card>

            <!-- 用户列表 -->
            <a-card title="用户列表" class="users-card" :bordered="false">
                <!-- 搜索和筛选 -->
                <template #extra>
                    <div class="search-filters">
                        <a-input-search v-model:value="searchQuery" placeholder="搜索用户名、昵称或邮箱" @search="handleSearch"
                            style="width: 280px;" allow-clear />
                    </div>
                </template>

                <!-- 用户表格 -->
                <a-table :columns="columns" :data-source="users" :pagination="pagination" :loading="loading"
                    @change="handleTableChange" row-key="user_id">
                    <!-- 头像列 -->
                    <template #bodyCell="{ column, record }">
                        <template v-if="column.key === 'avatar'">
                            <a-avatar :src="record.avatar_url" :size="40">
                                {{ (record.display_name || record.username)?.charAt(0)?.toUpperCase() }}
                            </a-avatar>
                        </template>

                        <!-- 用户信息列 -->
                        <template v-else-if="column.key === 'userInfo'">
                            <div class="user-info">
                                <div class="nickname">{{ record.display_name || '未设置昵称' }}</div>
                                <div class="username-small">@{{ record.username || '无用户名' }}</div>
                                <div class="email">{{ record.email }}</div>
                            </div>
                        </template>

                        <!-- 角色列 -->
                        <template v-else-if="column.key === 'role'">
                            <a-tag :color="record.role === 'admin' ? 'red' : 'blue'">
                                {{ record.role === 'admin' ? '管理员' : '普通用户' }}
                            </a-tag>
                        </template>

                        <!-- 注册时间列 -->
                        <template v-else-if="column.key === 'createdAt'">
                            {{ formatDate(record.created_at) }}
                        </template>

                        <!-- 操作列 -->
                        <template v-else-if="column.key === 'action'">
                            <a-space>
                                <a-button size="small" @click="viewUser(record)">详情</a-button>
                            </a-space>
                        </template>
                    </template>
                </a-table>
            </a-card>

            <!-- 用户详情模态框 -->
            <a-modal v-model:open="showUserDetail" title="用户详情" :footer="null" width="600px">
                <div v-if="selectedUser" class="user-detail">
                    <a-descriptions :column="2" bordered>
                        <a-descriptions-item label="头像" :span="2">
                            <a-avatar :src="selectedUser.avatar_url" :size="64">
                                {{ (selectedUser.display_name || selectedUser.username)?.charAt(0)?.toUpperCase() }}
                            </a-avatar>
                        </a-descriptions-item>
                        <a-descriptions-item label="昵称">
                            {{ selectedUser.display_name || '未设置昵称' }}
                        </a-descriptions-item>
                        <a-descriptions-item label="用户名">
                            {{ selectedUser.username || '未设置' }}
                        </a-descriptions-item>
                        <a-descriptions-item label="邮箱" :span="2">
                            {{ selectedUser.email }}
                        </a-descriptions-item>
                        <a-descriptions-item label="角色">
                            <a-tag :color="selectedUser.role === 'admin' ? 'red' : 'blue'">
                                {{ selectedUser.role === 'admin' ? '管理员' : '普通用户' }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item label="评论数">
                            {{ selectedUser.comments_count || 0 }}
                        </a-descriptions-item>
                        <a-descriptions-item label="注册时间">
                            {{ formatDate(selectedUser.created_at) }}
                        </a-descriptions-item>
                        <a-descriptions-item label="最后更新">
                            {{ formatDate(selectedUser.updated_at) }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>
            </a-modal>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, h } from 'vue'
import { message } from 'ant-design-vue'
import { ReloadOutlined } from '@ant-design/icons-vue'
import supabaseProfileService from '../../services/supabaseProfileService.js'
import AdminPageHeader from '../../components/admin/AdminPageHeader.vue'
import { Chart, registerables } from 'chart.js'

// 注册Chart.js组件
Chart.register(...registerables)

// 响应式数据
const loading = ref(false)
const users = ref([])
const totalUsers = ref(0)
const todayRegistered = ref(0)
const thisMonthRegistered = ref(0)
const searchQuery = ref('')
const showUserDetail = ref(false)
const selectedUser = ref(null)
const registrationChartRef = ref(null)
let registrationChart = null

// 分页配置
const pagination = reactive({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `共 ${total} 条记录`
})

// 表格列配置
const columns = [
    {
        title: '头像',
        key: 'avatar',
        width: 80,
        align: 'center'
    },
    {
        title: '用户信息',
        key: 'userInfo',
        width: 250
    },
    {
        title: '角色',
        key: 'role',
        width: 100,
        align: 'center'
    },
    {
        title: '注册时间',
        key: 'createdAt',
        width: 150,
        sorter: true
    },
    {
        title: '操作',
        key: 'action',
        width: 100,
        align: 'center'
    }
]

// 页面加载
onMounted(async () => {
    await Promise.all([
        loadUsers(),
        loadStats(),
        loadChart()
    ])
})

// 监听搜索变化
watch(searchQuery, () => {
    pagination.current = 1
    loadUsers()
}, { deep: true })

// 加载用户列表
async function loadUsers() {
    try {
        loading.value = true

        const [usersList, count] = await Promise.all([
            supabaseProfileService.getAllUsers(pagination.current, pagination.pageSize, searchQuery.value),
            supabaseProfileService.getUsersCount(searchQuery.value)
        ])

        users.value = usersList
        pagination.total = count
        totalUsers.value = count
    } catch (error) {
        console.error('加载用户列表失败:', error)
        message.error('加载用户列表失败')
    } finally {
        loading.value = false
    }
}

// 加载统计数据
async function loadStats() {
    try {
        // 获取今日注册用户数
        const today = new Date()
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
        const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000)

        const todayStats = await supabaseProfileService.getUserRegistrationStats(1)
        todayRegistered.value = todayStats[0]?.count || 0

        // 获取本月注册用户数
        const monthStats = await supabaseProfileService.getMonthlyUserStats(1)
        thisMonthRegistered.value = monthStats[0]?.count || 0
    } catch (error) {
        console.error('加载统计数据失败:', error)
    }
}

// 加载图表
async function loadChart() {
    try {
        const dailyStats = await supabaseProfileService.getUserRegistrationStats(30)
        renderRegistrationChart(dailyStats)
    } catch (error) {
        console.error('加载图表数据失败:', error)
    }
}

// 渲染注册趋势图表
function renderRegistrationChart(data) {
    const ctx = registrationChartRef.value?.getContext('2d')
    if (!ctx) return

    if (registrationChart) {
        registrationChart.destroy()
    }

    registrationChart = new Chart(ctx, {
        type: 'line',
        data: {
            labels: data.map(item => {
                const date = new Date(item.date)
                return `${date.getMonth() + 1}/${date.getDate()}`
            }),
            datasets: [{
                label: '新增用户',
                data: data.map(item => item.count),
                borderColor: '#1890ff',
                backgroundColor: 'rgba(24, 144, 255, 0.1)',
                tension: 0.4,
                fill: true
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {
                        stepSize: 1
                    }
                }
            },
            plugins: {
                legend: {
                    display: false
                }
            }
        }
    })
}

// 表格变化处理
function handleTableChange(paginationInfo) {
    pagination.current = paginationInfo.current
    pagination.pageSize = paginationInfo.pageSize
    loadUsers()
}

// 搜索处理
function handleSearch() {
    pagination.current = 1
    loadUsers()
}

// 查看用户详情
async function viewUser(user) {
    try {
        const userDetails = await supabaseProfileService.getUserDetails(user.user_id)
        selectedUser.value = userDetails
        showUserDetail.value = true
    } catch (error) {
        console.error('获取用户详情失败:', error)
        message.error('获取用户详情失败')
    }
}

// 格式化日期
function formatDate(dateString) {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    })
}
</script>

<style scoped>
.user-management {
    padding: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
}

.user-page-content {
    padding: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
}

/* 统计卡片 - 与分类管理、标签管理保持一致 */
.stats-cards {
    margin-bottom: 24px;
    width: 100%;
    max-width: 100%;
}

/* 图表卡片 */
.chart-card {
    margin-bottom: 24px;
    height: 300px;
    width: 100%;
    max-width: 100%;
}

.chart-container {
    height: 200px;
    position: relative;
    width: 100%;
    max-width: 100%;
}

/* 用户列表卡片 */
.users-card {
    margin-bottom: 24px;
    width: 100%;
    max-width: 100%;
}

.search-filters {
    display: flex;
    align-items: center;
}

.user-info .nickname {
    font-weight: 600;
    color: #262626;
    font-size: 16px;
}

.user-info .username-small {
    font-size: 12px;
    color: #8c8c8c;
}

.user-info .email {
    font-size: 12px;
    color: #8c8c8c;
}

.user-detail {
    margin-top: 16px;
}

/* 确保表格不会水平溢出 */
:deep(.ant-table-wrapper) {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
}

:deep(.ant-table) {
    min-width: 600px;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .user-management {
        padding: 0;
    }

    .search-filters {
        flex-direction: column;
        gap: 8px;
    }

    .search-filters .ant-input-search {
        width: 100% !important;
    }

    :deep(.ant-table) {
        min-width: 500px;
    }
}
</style>